const initialState = {
  cognitoUser: null,
  loading: {},
  saved: {},
  error: '',
  success: '',
  localUser: {},
  licenses: [],
  allLicenses: [],
  allUsers: [],
  friendlyId: '',
  file: {},
  showToc: true,
  issuingLicense: false,
  issuedLicense: {},
  // new state for the dashboard usage data
  licenseUsage: [],
  licenseUsageUsers: [],
  loadingLicenseUsage: false,
  // new state for the dashboard charts (usage / licenses time series / types)
  dashboardUsage: [],
  dashboardLicenses: [],
  dashboardTypes: [],
  loadingDashboard: false,
  // license types series (for stacked area / line chart)
  dashboardTypesSeries: [],
  dashboardTypesSeriesLabels: [],
  loadingLicenseTypesSeries: false
};

export default function reducers (state = initialState, action) {
  switch (action.type) {
    case 'SAVE_COGNITO_USER':
      return {
        ...state, cognitoUser: action.cognitoUser
      };
    case 'SET_LOGOUT_EXPLICIT':
      return {
        ...state,
        logoutExplicit: action.payload
      };
    case 'SET_SAVED':
      return {
        ...state, saved: { [action.key]: true }
      };
    case 'SET_ERROR':
      return {
        ...state, error: action.error
      };
    case 'SET_SUCCESS':
      return {
        ...state, success: action.success
      };
    case 'SET_FILE':
      return {
        ...state, file: action.file
      };
    case 'TOC_NOTICE':
      return {
        ...state, showToc: false
      };
    case 'CLEAR_LOADING':
      return {
        ...state, loading: { }
      };
    case 'SET_LOADING':
      return {
        ...state, loading: { ...state.loading, [action.key]: action.loading }
      };
    case 'SAVE_AUTH0_USER':
      return {
        ...state, auth0User: action.user
      };
    case 'SAVE_LOCAL_USER':
      return {
        ...state, localUser: action.user
      };
    case 'SAVE_LICENSES': {
      // compute hasStandalone here to keep state updates synchronous and simple
      const hasStandalone = (action.licenses || []).some(lic => lic.type === 'Team' || lic.type === 'Solo');
      return {
        ...state, licenses: action.licenses, hasStandalone: hasStandalone
      };
    }
    case 'SAVE_ALL_LICENSES':
      return {
        ...state, allLicenses: action.allLicenses
      };
    case 'SAVE_ALL_USERS':
      return {
        ...state, allUsers: action.allUsers
      };
    case 'SAVE_LABEL':
      return {
        ...state, friendlyId: action.friendlyId
      };
    case 'ISSUING_LICENSE':
      return {
        ...state, issuingLicense: true
      };
    case 'LICENSE_ISSUED':
      return {
        ...state,
        issuingLicense: false,
        issuedLicense: action.result
      };
    case 'CLEAR_LICENSE':
      return {
        ...state,
        issuedLicense: {}
      };
    case 'SET_UPVOTY_TOKEN':
      return {
        ...state,
        // Store only the token string here, not the entire action
        upvotyToken: action.upvotyToken
      };
    case 'SAVE_LICENSE_TYPE_STATS':
      return {
        ...state,
        licenseTypeStats: action.licenseTypeStats
      };

    case 'SET_HAS_STANDALONE':
      return {
        ...state,
        hasStandalone: action.hasStandalone
      };
    case 'DOWNLOAD_STANDALONE_INSTALLER':
      return {
        ...state,
        downloadingInstaller: true
      };
    case 'SAVE_USER_STATS':
      return {
        ...state,
        userStats: action.userStats
      };
    case 'SAVE_ACTIVE_PACKAGES':
      return {
        ...state,
        activePackages: action.packages
      };
    case 'SAVE_ACTIVE_MAC_PACKAGES':
      return {
        ...state,
        activeMacPackages: action.packages
      };
    case 'FETCH_ACTIVE_MAC_PACKAGES_FAILURE':
      return {
        ...state,
        error: action.error
      };
    case 'FETCH_ACTIVE_PACKAGES_FAILURE':
      return {
        ...state,
        error: action.error
      };

    case 'SAVE_PACKAGE_URL':
      return {
        ...state,
        packageUrlInfo: action.payload
      };
    case 'FETCH_PACKAGE_URL_FAILURE':
      return {
        ...state,
        error: action.error
      };

    case 'UPDATE_PACKAGE_URL_SUCCESS':
      return {
        ...state,
        success: JSON.stringify(action.payload)
      };
    case 'UPDATE_PACKAGE_URL_FAILURE':
      return {
        ...state,
        error: action.error
      };

    case 'SAVE_PLUGIN_MESSAGE':
      return {
        ...state,
        pluginMessage: action.payload
      };
    case 'FETCH_PLUGIN_MESSAGE_FAILURE':
      return {
        ...state,
        error: action.error
      };

    case 'UPDATE_PLUGIN_MESSAGE_SUCCESS':
      return {
        ...state,
        success: JSON.stringify(action.payload)
      };
    case 'UPDATE_PLUGIN_MESSAGE_FAILURE':
      return {
        ...state,
        error: action.error
      };
    case 'SAVE_LICENSE_USAGE':
      return {
        ...state,
        licenseUsage: [...(action.usage || [])]
      };
    case 'SAVE_LICENSE_USERS':
      return {
        ...state,
        licenseUsageUsers: [...(action.users || [])],
        loadingLicenseUsage: false
      };
    case 'FETCH_LICENSE_USAGE_REQUEST':
      return { ...state, loadingLicenseUsage: true };
    case 'FETCH_LICENSE_USAGE_LOADING':
      return { ...state, loadingLicenseUsage: true };
    case 'FETCH_LICENSE_USAGE_ERROR':
      return { ...state, loadingLicenseUsage: false };
    case 'FETCH_LICENSE_USAGE_SUCCESS': {
      const usage = Array.isArray(action.payload?.usage) ? action.payload.usage : [];
      const users = Array.isArray(action.payload?.users) ? action.payload.users : [];
      return {
        ...state,
        licenseUsage: [...usage],
        licenseUsageUsers: [...users],
        loadingLicenseUsage: false
      };
    }
    case 'FETCH_LICENSE_USAGE_FAILURE':
      return { ...state, loadingLicenseUsage: false };
    case 'FETCH_DASHBOARD_STATS_LOADING':
      return { ...state, loadingDashboard: true };
    case 'SAVE_DASHBOARD_STATS': {
      const usage = Array.isArray(action.payload?.usage) ? action.payload.usage : [];
      const licenses = Array.isArray(action.payload?.licenses) ? action.payload.licenses : [];
      const types = Array.isArray(action.payload?.types) ? action.payload.types : [];
      return {
        ...state,
        dashboardUsage: [...usage],
        dashboardLicenses: [...licenses],
        dashboardTypes: [...types],
        loadingDashboard: false
      };
    }
    case 'FETCH_LICENSE_TYPES_SERIES_LOADING':
      return { ...state, loadingLicenseTypesSeries: true };
    case 'SAVE_LICENSE_TYPES_SERIES': {
      const series = Array.isArray(action.payload?.series) ? action.payload.series : [];
      const labels = Array.isArray(action.payload?.labels) ? action.payload.labels : [];
      return {
        ...state,
        dashboardTypesSeries: [...series],
        dashboardTypesSeriesLabels: [...labels],
        loadingLicenseTypesSeries: false
      };
    }
    case 'FETCH_LICENSE_TYPES_SERIES_FAILURE':
      return { ...state, loadingLicenseTypesSeries: false };
    case 'FETCH_DASHBOARD_STATS_FAILURE':
      return { ...state, loadingDashboard: false };
    default:
      return state;
  }
}
